import { useCallback, useState } from "react";
import {
  appendProgressStep,
  emptyThreadLiveState,
  removeThreadTurn,
  type ThreadLiveState,
} from "./chat-session-helpers";
import type { PendingChatResponse } from "./types";

export const useThreadLiveStates = () => {
  const [liveStatesByThreadId, setLiveStatesByThreadId] = useState<
    Record<string, ThreadLiveState>
  >({});
  const [localRunningTurnsByThreadId, setLocalRunningTurnsByThreadId] =
    useState<Record<string, string>>({});

  const updateThreadLiveState = useCallback(
    (
      threadId: string,
      updater: (state: ThreadLiveState) => ThreadLiveState,
    ) => {
      setLiveStatesByThreadId((currentStates) => ({
        ...currentStates,
        [threadId]: updater(currentStates[threadId] ?? emptyThreadLiveState()),
      }));
    },
    [],
  );

  const markThreadFollowing = useCallback(
    (threadId: string, isFollowing: boolean) => {
      updateThreadLiveState(threadId, (state) => ({
        ...state,
        isFollowing,
      }));
    },
    [updateThreadLiveState],
  );

  const appendLiveTextDelta = useCallback(
    (threadId: string, delta: string) => {
      updateThreadLiveState(threadId, (state) => ({
        ...state,
        targetText: state.targetText + delta,
      }));
    },
    [updateThreadLiveState],
  );

  const appendLiveProgressStep = useCallback(
    (threadId: string, label: string) => {
      updateThreadLiveState(threadId, (state) => ({
        ...state,
        progressSteps: appendProgressStep(state.progressSteps, label),
      }));
    },
    [updateThreadLiveState],
  );

  const finishThreadTurn = useCallback(
    (threadId: string) => {
      setLocalRunningTurnsByThreadId((currentTurns) =>
        removeThreadTurn(currentTurns, threadId),
      );
      markThreadFollowing(threadId, false);
    },
    [markThreadFollowing],
  );

  const setPendingThreadResponse = useCallback(
    (threadId: string, pendingResponse: PendingChatResponse) => {
      updateThreadLiveState(threadId, (state) => ({
        ...state,
        pendingResponse,
        isFollowing: false,
      }));
    },
    [updateThreadLiveState],
  );

  const clearLiveResponse = useCallback((threadId?: string | null) => {
    if (!threadId) return;

    setLiveStatesByThreadId((currentStates) => ({
      ...currentStates,
      [threadId]: {
        ...(currentStates[threadId] ?? emptyThreadLiveState()),
        targetText: "",
        displayedText: "",
        progressSteps: [],
        pendingResponse: null,
      },
    }));
  }, []);

  return {
    liveStatesByThreadId,
    setLiveStatesByThreadId,
    localRunningTurnsByThreadId,
    setLocalRunningTurnsByThreadId,
    updateThreadLiveState,
    markThreadFollowing,
    appendLiveTextDelta,
    appendLiveProgressStep,
    finishThreadTurn,
    setPendingThreadResponse,
    clearLiveResponse,
  };
};

export type ThreadLiveStates = ReturnType<typeof useThreadLiveStates>;
